// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { InfoLabel, makeStyles, tokens } from '@fluentui/react-components';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Project } from '../../models';

const millisecondsPerDay = 24 * 60 * 60 * 1000;
const expirationPeriod = 365 * millisecondsPerDay;
const warningPeriod = 30 * millisecondsPerDay;

const useStyles = makeStyles({
    root: {
        paddingTop: tokens.spacingVerticalXS,
        color: tokens.colorStatusWarningForeground1,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        overflowX: 'hidden',
        overflowY: 'hidden',
    },
});

export type UnpublishWarningProps = Readonly<Pick<Project, 'publicUrl' | 'lastUpdated'>>;

/**
 * Project expiration (auto-unpublishing) warning label and info.
 */
export const UnpublishWarning: FC<UnpublishWarningProps> = (props) => {
    const { publicUrl, lastUpdated } = props;
    const styles = useStyles();

    const unpublishDate = useMemo(() => {
        if (publicUrl === undefined || lastUpdated === undefined) {
            return undefined;
        } else {
            const expirationDate = new Date(lastUpdated.getTime() + expirationPeriod);
            const warningTime = expirationDate.getTime() - warningPeriod;
            return new Date().getTime() > warningTime ? expirationDate : undefined;
        }
    }, [lastUpdated, publicUrl]);

    return (
        <>
            {unpublishDate !== undefined && (
                <InfoLabel
                    info={
                        <>
                            <FormattedMessage
                                defaultMessage="Your project has not been updated for a long time."
                                description="Message on about automatic project unpublishing"
                            />
                            <br />
                            <FormattedMessage
                                defaultMessage="It will be automatically unpublished on {UNPUBLISH_DATE}."
                                values={{ UNPUBLISH_DATE: unpublishDate.toLocaleDateString() }}
                                description="Message on about automatic project unpublishing"
                            />
                            <br />
                            <FormattedMessage
                                defaultMessage="To keep your project published, please make a change and republish your project."
                                description="Message on about automatic project unpublishing"
                            />
                        </>
                    }
                >
                    <span className={styles.root}>
                        <FormattedMessage
                            defaultMessage={'Will be unpublished on {UNPUBLISH_DATE}'}
                            values={{ UNPUBLISH_DATE: unpublishDate.toLocaleDateString() }}
                            description="Warning message for project auto-unpublishing"
                        />
                    </span>
                </InfoLabel>
            )}
        </>
    );
};

export default UnpublishWarning;
